import React from 'react';

import { CancelButton } from '@admin/components/rate_adjustment/schedulings/cancel';
import { Summary as RateAdjustmentSummary } from '@admin/components/rate_adjustment/schedulings/summary';
import { Spinner } from '@admin/components/spinner';
import { client } from '@admin/libraries/apollo';
import {
  AccountRateAdjustmentSchedulingFragment as RateAdjustment,
  RateAdjustment__Scheduling__CancelReason,
  RateAdjustment__Scheduling__State as AdjustmentState,
  useAccountRateAgreementSummaryQuery,
} from '@admin/schema';

const isUpcomingRateAdjustment = ({ state, notified }: RateAdjustment) => state === AdjustmentState.Pending && notified;
const isActiveRateAdjustment = ({ state }: RateAdjustment) => state === AdjustmentState.Active;
const isReversedRateAdjustment = (adjustment: RateAdjustment) => adjustment.state === AdjustmentState.Reversed;
const isWaivedRateAdjustment = ({ state, cancelReason }: RateAdjustment) =>
  state === AdjustmentState.Canceled && cancelReason === RateAdjustment__Scheduling__CancelReason.Waived;

const RateAdjustmentList: React.FC<{
  rateAdjustments: RateAdjustment[];
  onCancel(): void;
}> = ({ rateAdjustments, onCancel }) => (
  <ul>
    {rateAdjustments.map((rateAdjustment, index) => (
      <li key={index}>
        <RateAdjustmentSummary scheduling={rateAdjustment} />{' '}
        {rateAdjustment.state === AdjustmentState.Pending && (
          <CancelButton scheduling={rateAdjustment} onCancel={onCancel} />
        )}
        {rateAdjustment.notes && <div dangerouslySetInnerHTML={{ __html: rateAdjustment.notes }} />}
      </li>
    ))}
  </ul>
);

export const AccountRateAdjustments: React.FC<{
  accountID: string;
}> = ({ accountID }) => {
  const { data, loading, refetch } = useAccountRateAgreementSummaryQuery({ client, variables: { accountID } });

  if (loading) return <Spinner />;
  if (!data) return null;

  const rateAdjustments = data.account.rateAdjustments;

  const upcoming: RateAdjustment[] = rateAdjustments.filter(isUpcomingRateAdjustment);
  const active: RateAdjustment[] = rateAdjustments.filter(isActiveRateAdjustment);
  const waived: RateAdjustment[] = rateAdjustments.filter(isWaivedRateAdjustment);
  const reversed: RateAdjustment[] = rateAdjustments.filter(isReversedRateAdjustment);

  if (!upcoming.length && !active.length && !waived.length && !reversed.length) return null;

  return (
    <div>
      {upcoming.length > 0 && (
        <div>
          <strong>Rate Adjustments (Next):</strong>
          <RateAdjustmentList rateAdjustments={upcoming} onCancel={refetch} />
        </div>
      )}
      {active.length > 0 && (
        <div>
          <strong>Rate Adjustments (Past):</strong>
          <RateAdjustmentList rateAdjustments={active} onCancel={refetch} />
        </div>
      )}
      {waived.length > 0 && (
        <div>
          <strong>Rate Adjustments (Waived):</strong>
          <RateAdjustmentList rateAdjustments={waived} onCancel={refetch} />
        </div>
      )}
      {reversed.length > 0 && (
        <div>
          <strong>Rate Adjustments (Reversed):</strong>
          <RateAdjustmentList rateAdjustments={reversed} onCancel={refetch} />
        </div>
      )}
    </div>
  );
};
